import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Loader2, ArrowRight, Eye, EyeOff, Hourglass, Lightbulb, Store } from 'lucide-react';
import LanguageToggle from '@/components/common/LanguageToggle';
import useStore from '@/store/useStore';
import { useUiLanguage } from '@/lib/i18n';
import { apiMerchantLogin, apiMerchantRegister } from '@/services/api';

const FEATURES = [
  { icon: Hourglass, label: '5-Min Setup', iconClass: 'text-emerald-700', bgClass: 'bg-emerald-50' },
  { icon: Lightbulb, label: 'AI Insights', iconClass: 'text-amber-700', bgClass: 'bg-amber-50' },
  { icon: Store, label: 'POS Ready', iconClass: 'text-sky-700', bgClass: 'bg-sky-50' },
];

export default function MerchantLoginPage() {
  const navigate = useNavigate();
  const { t } = useUiLanguage();
  const { merchantLogin, merchantToken, merchantUser } = useStore();
  const [stage, setStage] = useState('entry');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!merchantToken || !merchantUser) return;
    if (merchantUser.onboarding_complete) navigate('/merchant/dashboard', { replace: true });
    else navigate('/merchant/onboarding', { replace: true });
  }, [merchantToken, merchantUser, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError(t('Email is required.'));
      return;
    }
    if (!password.trim()) {
      setError(t('Password is required.'));
      return;
    }
    if (stage === 'register' && !name.trim()) {
      setError(t('Name is required to get started.'));
      return;
    }

    setLoading(true);
    try {
      if (stage === 'login') {
        const data = await apiMerchantLogin(email.trim(), password);
        merchantLogin(data.token, data.merchant);
        if (data.merchant.onboarding_complete) navigate('/merchant/dashboard', { replace: true });
        else navigate('/merchant/onboarding', { replace: true });
      } else {
        const data = await apiMerchantRegister(email.trim(), password, name.trim());
        merchantLogin(data.token, data.merchant);
        navigate('/merchant/onboarding', { replace: true });
      }
    } catch (e2) {
      if (e2.code === 'ECONNABORTED') {
        setError(t('Request timed out. Please verify backend is running and try again.'));
      } else {
        const detail = e2.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : t('Unable to continue right now.'));
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#f7f7f4] px-6 py-10" data-testid="merchant-login-page">
      <div className="mx-auto max-w-[760px]">
        <div className="mb-4 flex justify-end">
          <LanguageToggle />
        </div>
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="rounded-[30px] border border-stone-200 bg-white/90 shadow-[0_20px_80px_rgba(0,0,0,0.08)] p-8 sm:p-12">
          <div className="mx-auto max-w-xl text-center space-y-5">
            <div className="mx-auto h-20 w-20 rounded-2xl bg-white border border-stone-200 shadow-sm flex items-center justify-center p-1.5">
              <img src="/assets/flyback.png" alt="FlyBack" className="h-full w-full rounded-xl object-contain" />
            </div>
            <h1 className="text-[36px] leading-tight font-bold text-[#0f172a]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
              {t('Turn casual diners into')}{' '}
              <span className="text-[#0f766e] italic">{t('loyal regulars.')}</span>
            </h1>
            <p className="text-sm font-semibold text-emerald-600">{t('Get onboarded in 5 minutes')}</p>
            <p className="text-sm text-stone-500 max-w-lg mx-auto">
              {t('The AI-powered loyalty platform designed for restaurant owners. Set up in minutes, integrate with your POS, and watch retention grow.')}
            </p>
          </div>

          {stage === 'entry' ? (
            <div className="mx-auto max-w-md mt-8 space-y-5">
              <button
                type="button"
                onClick={() => { setStage('register'); setError(''); }}
                data-testid="get-started-btn"
                className="w-full h-12 rounded-xl bg-[#0b132b] text-white font-semibold text-base hover:bg-[#111c3d] transition-colors flex items-center justify-center gap-2"
              >
                {t('Get started')} <ArrowRight className="w-4 h-4" />
              </button>
              <p className="text-center text-sm text-stone-500">
                {t('Already have an account?')}{' '}
                <button type="button" onClick={() => { setStage('login'); setError(''); }} className="font-semibold text-[#0b132b] underline-offset-2 hover:underline">
                  {t('Sign in')}
                </button>
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="mx-auto max-w-md mt-8 space-y-4">
              {stage === 'register' && (
                <div className="space-y-1.5">
                  <label className="text-xs font-bold uppercase tracking-wider text-stone-500">{t('Owner Name')}</label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('John Doe')} className="h-11 rounded-xl bg-stone-50 border-stone-200" data-testid="register-name-input" />
                </div>
              )}
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-stone-500">{t('Email')}</label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('owner@restaurant.com')} className="h-11 rounded-xl bg-stone-50 border-stone-200" data-testid="merchant-email-input" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-stone-500">{t('Password')}</label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('Enter your password')}
                    className="h-11 rounded-xl bg-stone-50 border-stone-200 pr-10"
                    data-testid="merchant-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-700"
                    data-testid="merchant-password-visibility-btn"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {error && <p className="text-sm text-red-600" data-testid="merchant-login-error">{error}</p>}

              <button
                type="submit"
                disabled={loading}
                data-testid="merchant-login-btn"
                className="w-full h-12 rounded-xl bg-[#0b132b] text-white font-semibold text-base hover:bg-[#111c3d] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (stage === 'login' ? t('Sign in') : t('Create account'))}
              </button>

              <p className="text-center text-sm text-stone-500">
                {stage === 'login' ? t("Don't have an account?") : t('Already have an account?')}{' '}
                <button
                  type="button"
                  onClick={() => { setStage(stage === 'login' ? 'register' : 'login'); setError(''); }}
                  className="font-semibold text-[#0b132b] underline-offset-2 hover:underline"
                >
                  {stage === 'login' ? t('Get started') : t('Sign in')}
                </button>
              </p>
            </form>
          )}

          <div className="mt-10 border-t border-stone-100 pt-6 grid grid-cols-3 gap-3 text-center">
            {FEATURES.map(({ icon: Icon, label, iconClass, bgClass }) => (
              <div key={label} className="space-y-2">
                <div className={`mx-auto h-8 w-8 rounded-full flex items-center justify-center ${bgClass}`}>
                  <Icon className={`w-4 h-4 ${iconClass}`} />
                </div>
                <p className="text-xs font-semibold text-stone-600">{t(label)}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-center text-xs text-stone-400">{t('By continuing, you agree to FlyBack Terms and Privacy Policy.')}</p>
        </motion.div>
      </div>
    </div>
  );
}
